import React, { memo, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { TicketIcon, User } from 'lucide-react';
import { toast } from 'sonner';
import { ticketsAPI } from '../../api';

const CreateTicketDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId,
  contactName,
  teamMembers = [],
  onSuccess 
}) => {
  const [form, setForm] = useState({
    subject: '',
    description: '',
    type: 'support',
    priority: 'medium',
    status: 'open',
    assigned_to: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }

    try {
      setLoading(true);
      
      await ticketsAPI.create({
        ...form,
        contact_id: contactId,
        contact_name: contactName
      });
      
      toast.success('Ticket created');
      setForm({
        subject: '',
        description: '',
        type: 'support',
        priority: 'medium',
        status: 'open',
        assigned_to: ''
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setLoading(false);
    }
  }, [form, contactId, contactName, onOpenChange, onSuccess]);

  const handleClose = useCallback(() => {
    setForm({
      subject: '',
      description: '',
      type: 'support',
      priority: 'medium',
      status: 'open',
      assigned_to: ''
    });
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TicketIcon className="h-5 w-5 text-orange-600" />
            Create Ticket
          </DialogTitle>
          <DialogDescription>
            Create a support ticket for {contactName || 'this contact'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ticket-subject">Subject *</Label>
            <Input
              id="ticket-subject"
              value={form.subject}
              onChange={(e) => setForm(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Brief description of the issue"
              data-testid="ticket-subject-input"
              autoFocus
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="ticket-description">Description</Label>
            <Textarea
              id="ticket-description"
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Detailed description..."
              rows={4}
              data-testid="ticket-description-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select 
                value={form.type} 
                onValueChange={(val) => setForm(prev => ({ ...prev, type: val }))}
              >
                <SelectTrigger data-testid="ticket-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="support">Support</SelectItem>
                  <SelectItem value="billing">Billing</SelectItem>
                  <SelectItem value="technical">Technical</SelectItem>
                  <SelectItem value="feature_request">Feature Request</SelectItem>
                  <SelectItem value="complaint">Complaint</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select 
                value={form.priority} 
                onValueChange={(val) => setForm(prev => ({ ...prev, priority: val }))}
              >
                <SelectTrigger data-testid="ticket-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Assign To</Label>
            <Select 
              value={form.assigned_to || "unassigned"} 
              onValueChange={(val) => setForm(prev => ({ ...prev, assigned_to: val === "unassigned" ? '' : val }))}
            >
              <SelectTrigger data-testid="ticket-assign-select">
                <User className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Select team member" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {teamMembers.map(member => (
                  <SelectItem key={member.id} value={member.id}>
                    {member.first_name} {member.last_name || member.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !form.subject.trim()} data-testid="create-ticket-submit">
              {loading ? 'Creating...' : 'Create Ticket'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});

CreateTicketDialog.displayName = 'CreateTicketDialog';

export default CreateTicketDialog;
